/**
 * CookieBanner Component
 *
 * GDPR-compliant cookie consent banner with granular preferences.
 */

import { useState, useEffect } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Cookie, Settings, Shield, BarChart3, Megaphone } from 'lucide-react';

// Simple UUID v4 generator (browser-compatible)
function generateVisitorId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

interface StoredConsent {
  version: string;
  prefs: CookiePreferences;
  acceptedAt: string;
}

const COOKIE_CONSENT_KEY = 'tabib_cookie_consent';
const VISITOR_ID_KEY = 'tabib_visitor_id';

/**
 * Reads the stored consent from localStorage. Tolerates the legacy
 * version-less shape `{essential, analytics, ...}` that earlier builds
 * wrote — those rows are treated as having an unknown version so the
 * banner re-prompts on the first load after this deploy.
 */
function readStoredConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'version' in parsed && 'prefs' in parsed) {
      return parsed as StoredConsent;
    }
    if (parsed && typeof parsed === 'object' && 'essential' in parsed) {
      return {
        version: 'legacy',
        prefs: parsed as CookiePreferences,
        acceptedAt: '',
      };
    }
  } catch {
    /* fall through */
  }
  return null;
}

export function CookieBanner() {
  const { language, isRTL } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    functional: true,
  });

  useEffect(() => {
    // Fetch the current cookie-policy version from the server, then compare
    // against whatever the visitor previously accepted. We re-prompt when
    // either nothing is stored or the stored version is older than the
    // current `POLICY_VERSIONS.cookie_policy` on the backend.
    let cancelled = false;
    (async () => {
      let serverVersion = 'unknown';
      try {
        const res = await fetch('/api/legal/policies', { credentials: 'include' });
        if (res.ok) {
          const json = await res.json();
          serverVersion = json?.versions?.cookie_policy ?? 'unknown';
        }
      } catch {
        /* offline — fall back to localStorage check below */
      }
      if (cancelled) return;
      setCurrentVersion(serverVersion);

      const stored = readStoredConsent();
      if (stored) {
        setPreferences(stored.prefs);
      }

      const needsPrompt =
        !stored || stored.version === 'legacy' || stored.version !== serverVersion;
      if (needsPrompt) {
        setTimeout(() => {
          if (!cancelled) setShowBanner(true);
        }, 500);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getOrCreateVisitorId = (): string => {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = generateVisitorId();
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }
    return visitorId;
  };

  const savePreferences = async (prefs: CookiePreferences) => {
    const version = currentVersion ?? 'unknown';
    const record: StoredConsent = {
      version,
      prefs,
      acceptedAt: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(record));
    setPreferences(prefs);
    setShowBanner(false);
    setShowPreferences(false);

    // Save granular preferences (visitor-scoped, no auth required).
    // `apiRequest` rather than a bare `fetch`: `apiCsrfProtection`
    // (`server/middleware/csrf.ts:239`) 403s any state-changing request without
    // `X-Requested-With`, so in production this POST never reached the server —
    // the banner reported success while consent was never recorded. It throws on
    // a non-OK response, which the catch below turns back into best-effort.
    try {
      const visitorId = getOrCreateVisitorId();
      await apiRequest('POST', '/api/legal/cookie-preferences', {
        visitorId,
        ...prefs,
      });
    } catch (error) {
      console.error('Failed to save cookie preferences to server:', error);
    }

    // Best-effort: when the visitor is signed in, also record an explicit
    // versioned consent against the cookie policy itself so it sits next to
    // privacy / terms / disclaimer in the legal consent ledger. The endpoint
    // is auth-protected; for an anonymous visitor it answers 401, which
    // `apiRequest` raises as a throw rather than a falsy `res.ok`. The catch
    // keeps that path silent — the preferences row above is the record that
    // matters for a visitor who is not signed in.
    try {
      await apiRequest('POST', '/api/legal/consent', {
        documentType: 'cookie_policy',
        documentVersion: version,
      });
    } catch {
      /* anonymous visitor (401) or transport failure — banner must not break */
    }
  };

  const handleAcceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    });
  };

  const handleRejectNonEssential = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
      functional: true,
    });
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div
        className={`fixed right-0 bottom-0 left-0 z-50 border-t bg-white p-4 shadow-lg md:p-6 ${
          isRTL ? 'rtl' : 'ltr'
        }`}
        role="dialog"
        aria-label={language === 'ar' ? 'إعدادات ملفات تعريف الارتباط' : 'Cookie Settings'}
      >
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
            {/* Icon and Text */}
            <div className="flex flex-1 items-start gap-3">
              <div className="flex-shrink-0 rounded-full bg-blue-100 p-2">
                <Cookie className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="mb-1 font-semibold text-gray-900">
                  {language === 'ar' ? 'نحن نستخدم ملفات تعريف الارتباط' : 'We use cookies'}
                </h3>
                <p className="text-sm text-gray-600">
                  {language === 'ar'
                    ? 'نستخدم ملفات تعريف الارتباط لتحسين تجربتك وتحليل استخدام الموقع. يمكنك تخصيص تفضيلاتك أو قبول الكل.'
                    : 'We use cookies to improve your experience and analyze site usage. You can customize your preferences or accept all.'}
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex w-full flex-wrap gap-2 md:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPreferences(true)}
                className="flex-1 md:flex-none"
              >
                <Settings className="mr-2 h-4 w-4" />
                {language === 'ar' ? 'تخصيص' : 'Customize'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectNonEssential}
                className="flex-1 md:flex-none"
              >
                {language === 'ar' ? 'الأساسية فقط' : 'Essential Only'}
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="bg-medical-primary flex-1 hover:bg-blue-700 md:flex-none"
              >
                {language === 'ar' ? 'قبول الكل' : 'Accept All'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences Dialog */}
      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className={`max-w-md ${isRTL ? 'rtl' : 'ltr'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {language === 'ar' ? 'إعدادات ملفات تعريف الارتباط' : 'Cookie Preferences'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar'
                ? 'اختر أنواع ملفات تعريف الارتباط التي تريد السماح بها'
                : 'Choose which types of cookies you want to allow'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Essential Cookies */}
            <div className="flex items-start justify-between gap-4 rounded-lg bg-gray-50 p-3">
              <div className="flex items-start gap-3">
                <Shield className="mt-0.5 h-5 w-5 text-green-600" />
                <div>
                  <h4 className="font-medium text-gray-900">
                    {language === 'ar' ? 'أساسية' : 'Essential'}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {language === 'ar'
                      ? 'ضرورية لعمل الموقع. لا يمكن تعطيلها.'
                      : 'Required for the site to work. Cannot be disabled.'}
                  </p>
                </div>
              </div>
              <Switch checked={true} disabled aria-label="Essential cookies" />
            </div>

            {/* Functional Cookies */}
            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="flex items-start gap-3">
                <Settings className="mt-0.5 h-5 w-5 text-blue-600" />
                <div>
                  <h4 className="font-medium text-gray-900">
                    {language === 'ar' ? 'وظيفية' : 'Functional'}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {language === 'ar'
                      ? 'تذكر تفضيلاتك مثل اللغة والمنطقة الزمنية.'
                      : 'Remember your preferences like language and timezone.'}
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.functional}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, functional: checked })
                }
                aria-label="Functional cookies"
              />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="flex items-start gap-3">
                <BarChart3 className="mt-0.5 h-5 w-5 text-purple-600" />
                <div>
                  <h4 className="font-medium text-gray-900">
                    {language === 'ar' ? 'تحليلية' : 'Analytics'}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {language === 'ar'
                      ? 'تساعدنا على فهم كيفية استخدام الموقع.'
                      : 'Help us understand how the site is used.'}
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, analytics: checked })
                }
                aria-label="Analytics cookies"
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="flex items-start gap-3">
                <Megaphone className="mt-0.5 h-5 w-5 text-orange-600" />
                <div>
                  <h4 className="font-medium text-gray-900">
                    {language === 'ar' ? 'تسويقية' : 'Marketing'}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {language === 'ar'
                      ? 'تُستخدم لعرض إعلانات مخصصة.'
                      : 'Used to show personalized advertisements.'}
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, marketing: checked })
                }
                aria-label="Marketing cookies"
              />
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={handleRejectNonEssential}>
              {language === 'ar' ? 'رفض غير الأساسية' : 'Reject Non-Essential'}
            </Button>
            <Button onClick={handleSavePreferences} className="bg-medical-primary">
              {language === 'ar' ? 'حفظ التفضيلات' : 'Save Preferences'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Hook to check cookie consent status
 */
export function useCookieConsent() {
  const [consent, setConsent] = useState<CookiePreferences | null>(null);

  useEffect(() => {
    const stored = readStoredConsent();
    setConsent(stored?.prefs ?? null);
  }, []);

  return {
    hasConsent: consent !== null,
    consent,
    canUseAnalytics: consent?.analytics ?? false,
    canUseMarketing: consent?.marketing ?? false,
  };
}
